import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Narration, NarrationClip } from './types'
import { clipAt, clipsTimeline, isPieceNarration, totalDuration, type ClipSpan } from './narration'
import { claim, isDemoAudio, release, useAudioPlayer, type AudioPlayer, type PlaybackOwner } from './useAudioPlayer'

// One player for either kind of narration (lib/narration.ts), on one global
// timeline: `time`, `duration` and `seek` are the whole reading's, whether it
// is a single file or pieces recorded paragraph by paragraph.
//
// A single file plays through useAudioPlayer. Pieces play back to back: the
// current piece plays in one <audio> element while the next one preloads in a
// second, and at the end of a piece the two swap, so the gap between pieces
// is as small as the browser allows. `demo:` pieces are silent clocks, as
// everywhere else in the local demo. It joins the same one-at-a-time rule as
// every other player: starting it pauses whatever else was playing.

export interface SequenceState {
  playing: boolean
  /** Seconds on the whole narration's timeline. */
  time: number
  error: string | null
}

const BLOCKED = 'Playback was blocked. Tap play again.'
const BROKEN = 'This recording could not be played.'

/** Plays clips back to back. Framework-free; useNarrationPlayer wraps it for React. */
export class ClipSequencer implements PlaybackOwner {
  readonly spans: ClipSpan[]
  readonly duration: number
  private index = 0
  /** Seconds into the current clip (the demo clock's position, and where a paused element resumes). */
  private offset = 0
  private rate = 1
  private playing = false
  private error: string | null = null
  private readonly els: Array<HTMLAudioElement | null> = [null, null]
  /** Which clip each element holds (-1: none). */
  private readonly holds = [-1, -1]
  /** The element playing the current clip. */
  private cur = 0
  private raf = 0
  private last = 0
  private readonly cleanups: Array<() => void> = []
  private destroyed = false

  constructor(
    clips: NarrationClip[],
    private readonly emit: (s: SequenceState) => void,
  ) {
    this.spans = clipsTimeline(clips)
    this.duration = totalDuration(clips)
  }

  /** The exact position right now, in global seconds. */
  currentTime(): number {
    const span = this.spans[this.index]
    if (!span) return 0
    let within = this.offset
    const el = this.els[this.cur]
    if (!this.isDemo(this.index) && el && this.holds[this.cur] === this.index) {
      if (el.ended) within = Infinity
      else if (Number.isFinite(el.currentTime)) within = el.currentTime
    }
    return span.start + Math.min(Math.max(0, within), span.end - span.start)
  }

  play(): void {
    if (this.destroyed || this.spans.length === 0) return
    if (this.currentTime() >= this.duration - 0.05) this.moveTo(0)
    claim(this)
    this.playing = true
    this.error = null
    this.startCurrent()
    this.notify()
  }

  pause(): void {
    if (!this.playing) return
    this.playing = false
    release(this)
    this.stopClock()
    const el = this.els[this.cur]
    if (el && this.holds[this.cur] === this.index) {
      if (Number.isFinite(el.currentTime)) this.offset = el.currentTime
      el.pause()
    }
    this.notify()
  }

  seek(seconds: number): void {
    if (this.destroyed || this.spans.length === 0) return
    const wasPlaying = this.playing
    const other = this.els[1 - this.cur]
    if (other && !other.paused) other.pause()
    this.moveTo(seconds)
    if (wasPlaying) this.startCurrent()
    this.notify()
  }

  setRate(rate: number): void {
    this.rate = rate
    for (const el of this.els) {
      if (!el) continue
      el.defaultPlaybackRate = rate
      el.playbackRate = rate
    }
  }

  destroy(): void {
    this.destroyed = true
    this.pause()
    this.stopClock()
    this.cleanups.forEach((fn) => fn())
    for (const el of this.els) {
      if (!el) continue
      el.pause()
      el.removeAttribute('src')
    }
    release(this)
  }

  // ── internals ──────────────────────────────────────────────────────────

  private isDemo(i: number): boolean {
    return isDemoAudio(this.spans[i]?.clip.src)
  }

  private notify(): void {
    if (!this.destroyed) this.emit({ playing: this.playing, time: this.currentTime(), error: this.error })
  }

  /** Point at global `seconds` without starting anything. */
  private moveTo(seconds: number): void {
    const at = clipAt(this.spans, seconds)
    if (!at) return
    const el = this.els[this.cur]
    if (el && at.index !== this.index && !el.paused) el.pause()
    this.stopClock()
    this.index = at.index
    this.offset = at.offset
    if (!this.isDemo(at.index)) {
      // Seeking into the piece that is preloading: use that element.
      if (this.holds[this.cur] !== at.index && this.holds[1 - this.cur] === at.index) this.cur = 1 - this.cur
      const target = this.load(this.cur, at.index)
      if (target) this.setPosition(target, at.offset)
    }
  }

  private element(k: number): HTMLAudioElement | null {
    if (this.els[k]) return this.els[k]
    if (typeof Audio === 'undefined') return null
    const el = new Audio()
    el.preload = 'auto'
    el.defaultPlaybackRate = this.rate
    el.playbackRate = this.rate
    const onTime = () => {
      if (el !== this.els[this.cur] || this.holds[this.cur] !== this.index) return
      this.offset = el.currentTime
      this.notify()
    }
    const onEnded = () => {
      if (el === this.els[this.cur] && this.playing) this.advance()
    }
    const onPause = () => {
      // Paused from outside (the OS media keys): reflect it. Our own pauses set
      // `playing` first, and a finished clip is handled by onEnded.
      if (el === this.els[this.cur] && this.playing && el.paused && !el.ended) {
        this.playing = false
        release(this)
        this.notify()
      }
    }
    const onError = () => {
      if (el !== this.els[this.cur] || this.holds[this.cur] !== this.index) return
      this.playing = false
      release(this)
      this.error = BROKEN
      this.notify()
    }
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('ended', onEnded)
    el.addEventListener('pause', onPause)
    el.addEventListener('error', onError)
    this.cleanups.push(() => {
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('error', onError)
    })
    this.els[k] = el
    return el
  }

  /** Element `k` holding clip `i` (loading it when it holds something else). */
  private load(k: number, i: number): HTMLAudioElement | null {
    const el = this.element(k)
    if (!el) return null
    if (this.holds[k] !== i) {
      el.src = this.spans[i].clip.src
      this.holds[k] = i
    }
    return el
  }

  private setPosition(el: HTMLAudioElement, seconds: number): void {
    try {
      el.currentTime = seconds
    } catch {
      /* not seekable yet: applied once metadata is in */
    }
    if (seconds > 0 && el.readyState < 1) {
      const apply = () => {
        el.removeEventListener('loadedmetadata', apply)
        try {
          el.currentTime = seconds
        } catch {
          /* ignore */
        }
      }
      el.addEventListener('loadedmetadata', apply)
    }
  }

  private startCurrent(): void {
    if (!this.playing) return
    if (this.isDemo(this.index)) {
      this.startClock()
    } else {
      const el = this.load(this.cur, this.index)
      if (!el) {
        // No audio here at all (tests without an element): behave like a clock.
        this.startClock()
        return
      }
      el.playbackRate = this.rate
      const started = el.play() as Promise<void> | undefined
      started?.catch?.((e: { name?: string } | undefined) => {
        // A src swap aborts the previous play() — not a failure.
        if (e?.name === 'AbortError' || this.destroyed) return
        this.playing = false
        release(this)
        this.error = BLOCKED
        this.notify()
      })
    }
    this.preloadNext()
  }

  private preloadNext(): void {
    const next = this.index + 1
    if (next >= this.spans.length || this.isDemo(next)) return
    this.load(1 - this.cur, next)
  }

  /** The current clip finished: on to the next, or stop at the end. */
  private advance(): void {
    if (this.index + 1 >= this.spans.length) {
      this.playing = false
      release(this)
      this.stopClock()
      this.offset = this.spans[this.index].end - this.spans[this.index].start
      this.notify()
      return
    }
    this.index += 1
    this.offset = 0
    if (!this.isDemo(this.index)) {
      if (this.holds[1 - this.cur] === this.index) this.cur = 1 - this.cur
      const el = this.load(this.cur, this.index)
      if (el) this.setPosition(el, 0)
    }
    this.startCurrent()
    this.notify()
  }

  private startClock(): void {
    this.stopClock()
    this.last = performance.now()
    const tick = () => {
      if (!this.playing) return
      // performance.now(), not the frame's timestamp: some engines (jsdom) time those from another origin.
      const now = performance.now()
      const span = this.spans[this.index]
      this.offset += (Math.max(0, now - this.last) / 1000) * this.rate
      this.last = now
      if (this.offset >= span.end - span.start) {
        this.raf = 0
        this.advance()
        return
      }
      this.notify()
      this.raf = requestAnimationFrame(tick)
    }
    this.raf = requestAnimationFrame(tick)
  }

  private stopClock(): void {
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
  }
}

const IDLE: SequenceState = { playing: false, time: 0, error: null }

/** Clips as one player; null clips = an idle player. */
function useClipsPlayer(clips: NarrationClip[] | null): AudioPlayer {
  const key = clips ? clips.map((c) => `${c.id}|${c.src}|${c.duration}`).join(',') : ''
  // Rebuild only when the pieces themselves change, not on every new array.
  const stable = useMemo(() => clips, [key]) // eslint-disable-line react-hooks/exhaustive-deps
  const [state, setState] = useState<SequenceState>(IDLE)
  const [rate, setRateState] = useState(1)
  const seq = useRef<ClipSequencer | null>(null)
  const rateRef = useRef(1)
  const lastTime = useRef(0)
  lastTime.current = state.time

  useEffect(() => {
    if (!stable || stable.length === 0) {
      seq.current = null
      setState(IDLE)
      return
    }
    const s = new ClipSequencer(stable, setState)
    s.setRate(rateRef.current)
    seq.current = s
    // Keep the place when the pieces change under a paused player (a re-recorded piece).
    const keep = Math.min(lastTime.current, s.duration)
    if (keep > 0) s.seek(keep)
    else setState({ playing: false, time: 0, error: null })
    return () => {
      s.destroy()
      if (seq.current === s) seq.current = null
    }
  }, [stable])

  const play = useCallback(() => seq.current?.play(), [])
  const pause = useCallback(() => seq.current?.pause(), [])
  const seek = useCallback((t: number) => seq.current?.seek(t), [])
  const setRate = useCallback((r: number) => {
    rateRef.current = r
    setRateState(r)
    seq.current?.setRate(r)
  }, [])
  const currentTime = useCallback(() => seq.current?.currentTime() ?? 0, [])
  const duration = stable ? totalDuration(stable) : 0

  return {
    playing: state.playing,
    time: state.time,
    duration,
    rate,
    error: state.error,
    play,
    pause,
    toggle: () => (state.playing ? pause() : play()),
    seek,
    setRate,
    currentTime,
  }
}

/**
 * The narration's player, for either kind: a single file (useAudioPlayer) or
 * pieces (clips back to back). Same interface, one global timeline.
 */
export function useNarrationPlayer(narration: Narration | null | undefined): AudioPlayer {
  const pieces = isPieceNarration(narration)
  const single = useAudioPlayer(pieces ? null : (narration?.src ?? null), pieces ? 0 : (narration?.duration ?? 0))
  const inPieces = useClipsPlayer(pieces ? narration.clips : null)
  return pieces ? inPieces : single
}
